// Workstream B #34 — engine-aware relation-identity comparator (CK-B MUST-FIX 5).
//
// Candidate retrieval in the identifier index is deliberately BROAD (a
// leaf-name lookup that over-collects across schemas / databases / case). The
// FINAL decision of whether a recorded relation reference actually denotes the
// rename/find-references target — and with what confidence — is made here, with
// per-engine identifier-equality semantics:
//
//   - PostgreSQL / Oracle: unquoted folds (lower / upper); quoted preserves
//     case. The recorded `foldedName` etc. already encode that, so component
//     comparison is case-SENSITIVE on the folded forms.
//   - SQLite: identifiers are ASCII-case-insensitive, even quoted.
//   - MSSQL: the collation of the DATABASE that holds the object controls
//     equality, and delimiters do not force case (`dbo.[V]` and `dbo.V` name
//     one object in every collation). We default to case-INSENSITIVE (the
//     common server default). `mssqlCatalogCaseSensitive` is now wired
//     end-to-end: `mssqlCollationProbe.ts` reads each database's collation on
//     the lifecycle's case-policy gate, the lifecycle hands it to that
//     database's index as its object-key rule, and Find References
//     (`findReferencesService.ts`) / Peek / Rename (`rename/renamePlanner.ts`)
//     read it back off the index handle through `identityCaseContext.ts`'s
//     {@link mssqlCatalogCaseSensitiveFor}.
//     Unlike every other engine, the case-sensitive comparison here is on the
//     SPELLED components, not the folded ones — see `identityCaseContext.ts`'s
//     {@link catalogComponentEq} for why the folded forms cannot answer this
//     question on SQL Server.
//     The rule is not relation-only: SQL Server compares routine, column,
//     schema and table names in the ONE collation of the holding database, so
//     the index's exact-match lookup (columns / routines) is narrowed with the
//     same decision through `catalogLeafCase.ts`'s {@link catalogLeafRefMatches}.
//   - MySQL: table/database equality follows `@@lower_case_table_names`
//     (`0` = case-sensitive, `1`/`2` = case-insensitive), regardless of
//     quoting. It reaches only what the filesystem holds — a schema is a
//     DIRECTORY and a table / view a FILE — so a stored ROUTINE's own name and
//     a COLUMN name stay case-insensitive in every mode
//     (`identityCaseContext.ts`'s `MYSQL_CASE_INSENSITIVE_PARTS`), which is
//     also how `mysqlCaseProbe.ts`'s `mysqlObjectKeyCase` keys the index — the
//     comparator and the index must give columns the same answer.
//     Unlike mssql, this ONE **is** wired end-to-end (task-review
//     finding 1, fix round 1): `mysqlCaseProbe.ts`'s
//     `getMysqlLowerCaseTableNamesFor` reads the value the probe at
//     `connections/services/language-services/mysqlSchemaIntrospection.ts`
//     (`fetchSessionVars`) already folds into the connection's cached
//     `MysqlSchemaSnapshot` (`mysqlSchemaCache.ts`), and every production
//     `RelationIdentityContext` builder (`renameProvider.ts`'s
//     `defaultOpenPreview`, `renamePreviewStore.ts`'s `submitNewName`,
//     `openReferencesTab.ts`) threads it in. Undefined (engine mismatch, or
//     the connection's mysql schema cache hasn't warmed yet / probe failed) is
//     treated as case-insensitive (managed-MySQL / macOS / Windows default) so
//     discovery never misses a candidate.
//     Parse-time folding is deliberately NOT part of that wiring, and does not
//     need to be. `sql-resolver/scope/identifiers.ts`'s `defaultFolding` still
//     lowercases every unquoted mysql identifier without consulting
//     `lower_case_table_names`, so an unquoted `Users` and an unquoted `users`
//     do collide to the same `foldedName`. Mode 0 no longer reads that fold:
//     `identityCaseContext.ts`'s `catalogComponentEq` compares the SPELLED
//     halves there (a backticked identifier no more changes a MySQL identity
//     than brackets change a T-SQL one), so `Users`, `` `Users` `` and the
//     catalog's `Users` are one table while `users` is another — proved end to
//     end, against the real parser and the real Object-Explorer builder, in
//     `identityCaseContext.test.ts` § "mode 0 (case-sensitive — the Linux
//     default)".
//
// This module owns NO index lookup — it is a pure predicate over two already-
// parsed identities. Callers (rename planner + find-references) pair it with the
// index's `queryRelationCandidates` broad lookup. The single exception is
// `identityCaseContext.ts`'s {@link mssqlCatalogCaseSensitiveFor}, which reads
// one POLICY value off an index handle (not records) so both callers derive the
// SQL Server case rule the same way.
//
// The internals live in two siblings, re-exported here so that no caller has to
// know which one holds what:
//   - `identityCaseContext.ts` — the per-engine case context and the
//     one-component equality primitives every comparator is decided with.
//   - `catalogLeafCase.ts` — the same narrowing for the kinds the index answers
//     with its exact-match lookup (columns / routines), plus
//     {@link columnLeavesEqual}: the COLUMN-leaf equality every caller asking
//     "is this token the renamed column?" must use instead of
//     {@link relationIdentitiesEqual}, whose leaf is a RELATION leaf and is
//     case-SIGNIFICANT on a MySQL `lower_case_table_names = 0` server.
//
// SCOPE — synonyms are OUT (v1, follow-up slice; CK-B verdict MUST-FIX 4): this
// comparator matches DIRECT table/view references by their spelled identity. A
// body that reaches the renamed object through a SYNONYM / alias-object (MSSQL
// `CREATE SYNONYM`, Oracle synonym) is not resolved to its base object — the
// synonym's own name is what the walker recorded, so renaming the base table
// does not rewrite bodies that reference it via a synonym. The preview dialog
// remains the net for anything not rewritten. Synonym-to-base resolution is a
// separate future slice (see Ralph/StructuredSqlAdoption/PRD.md §8 PM-B).

import {
  catalogComponentEq,
  databaseComponent,
  databaseComponentEq,
  foldedLeaf,
  leafComponent,
  nonEmpty,
  schemaComponent,
  toDatabaseComponent,
  type DatabaseComponent,
  type EffectiveDatabase,
  type RelationIdentityContext,
} from './identityCaseContext';
import { foldOeComponent } from './oeIdentifier';
import type { MatchConfidence, QualifiedName } from './types';

export { catalogLeafRefMatches, columnLeavesEqual } from './catalogLeafCase';
export {
  componentsEqual,
  mssqlCatalogCaseSensitiveFor,
  qualifiedComponent,
  relationIdentityContextFor,
} from './identityCaseContext';
export type {
  DatabaseComponent,
  EffectiveDatabase,
  IdentityComponent,
  RelationIdentityContext,
} from './identityCaseContext';
export type { MatchConfidence };

/** The database a rename / find-references resolves against, in BOTH
 *  spellings: the target's own explicit database when it carries one, else the
 *  connection's current database folded for this engine. `undefined` when
 *  neither is known — the database check is then skipped entirely.
 *
 *  Shared by the rename planner and find-references so the two cannot disagree
 *  about which database a bare target belongs to.
 *
 *  `connectionDatabase` is a CATALOG spelling — the connection's / tab's own
 *  database, never source text — so it is folded through
 *  {@link foldOeComponent}, the same helper every other Object-Explorer catalog
 *  component goes through, and NOT as an unquoted identifier. On the two
 *  engines that fold, a catalog name whose case differs from the fold can only
 *  have been created quoted, so re-folding it destroys it: a PostgreSQL
 *  database genuinely named `Sales` arrived here as `sales`, and a buffer's
 *  correct `"Sales".public.users` — the only spelling PostgreSQL accepts for it
 *  — then compared as a DIFFERENT database. The relation arm dropped that
 *  reference silently and the column arm reported it as a skip, both for a
 *  reference that should have been rewritten. mssql / mysql / sqlite are
 *  unaffected either way: their database compare is either case-insensitive or
 *  decided on the spelled half. */
export function effectiveDatabaseFor(
  target: QualifiedName,
  connectionDatabase: string | undefined,
  ctx: RelationIdentityContext,
): DatabaseComponent | undefined {
  if (target.foldedDatabase) return databaseComponent(target);
  if (!connectionDatabase) return undefined;
  return {
    spelled: connectionDatabase,
    folded: foldOeComponent(ctx.engine, connectionDatabase),
  };
}

/** Per-engine leaf-name equality — the broad candidate key. Case-insensitive
 *  everywhere here on purpose: it is used only to widen the candidate set; the
 *  precise per-component decision lives in {@link matchRelationRef}. */
export function relationLeafKey(name: QualifiedName): string {
  return foldedLeaf(name).toLowerCase();
}

export type RelationMatch = { readonly confidence: MatchConfidence };

/** Decide whether a recorded relation reference (`ref`, with the dot-part count
 *  of the source spelling) denotes `target`, and with what confidence.
 *
 *  Returns `null` when the reference is DEFINITELY a different relation (a
 *  differing explicitly-present schema / database, or a differing leaf name).
 *
 *  - `exact`      — schema-qualified reference whose schema (and database, when
 *                   both carry one) matches; or an engine with no schema
 *                   dimension (mysql) matching by name.
 *  - `contextual` — a schema-qualified reference matched against an
 *                   unschema-qualified target: the schema is resolved via
 *                   effective context, not strictly verified.
 *  - `ambiguous`  — an unqualified (bare) reference against a schema-qualified
 *                   target, or a 4-part linked-server reference (whose server
 *                   component the model cannot represent).
 *
 *  `effectiveTargetDb` is the database the rename resolves against — build it
 *  with {@link effectiveDatabaseFor}, which carries the SPELLED half SQL Server
 *  needs on a `_CS_` instance alongside the fold. Pass undefined when unknown
 *  (the database check is then skipped). */
export function matchRelationRef(
  ref: QualifiedName,
  refPartCount: number | undefined,
  target: QualifiedName,
  effectiveTargetDb: EffectiveDatabase | undefined,
  ctx: RelationIdentityContext,
): RelationMatch | null {
  if (!catalogComponentEq(leafComponent(ref), leafComponent(target), ctx)) return null;

  // 4-part `server.db.schema.name`: the server identity is dropped by the
  // parser, so we cannot verify this reference targets the same linked server.
  if ((refPartCount ?? 0) >= 4) return { confidence: 'ambiguous' };

  const refSchema = nonEmpty(ref.foldedSchema);
  const targetSchema = nonEmpty(target.foldedSchema);

  if (refSchema && targetSchema) {
    // Presence is decided on the folded component (as it always was); only the
    // EQUALITY test is collation-aware.
    if (!catalogComponentEq(schemaComponent(ref), schemaComponent(target), ctx)) return null;
    const refDb = databaseComponent(ref);
    const tgtDb = toDatabaseComponent(effectiveTargetDb);
    if (refDb.folded && tgtDb.folded && !databaseComponentEq(refDb, tgtDb, ctx)) return null; // different db
    return { confidence: 'exact' };
  }
  if (refSchema && !targetSchema) return { confidence: 'contextual' };
  if (!refSchema && targetSchema) return { confidence: 'ambiguous' };
  // Neither side schema-qualified (mysql-style, or a bare rename): a name match
  // is as specific as the identity model allows.
  return { confidence: 'exact' };
}

/** True when two relation identities are the SAME relation under this engine's
 *  rules — every component present on BOTH sides matches. A component missing on
 *  either side is a wildcard (never a mismatch), so a bare `users` is treated as
 *  the same relation as `dbo.users`. Used by the rename planner's column-host
 *  disambiguation in place of the old unconditional `.toLowerCase()` compare. */
export function relationIdentitiesEqual(
  a: QualifiedName,
  b: QualifiedName,
  ctx: RelationIdentityContext,
): boolean {
  if (!catalogComponentEq(leafComponent(a), leafComponent(b), ctx)) return false;
  const aSchema = nonEmpty(a.foldedSchema);
  const bSchema = nonEmpty(b.foldedSchema);
  if (aSchema && bSchema && !catalogComponentEq(schemaComponent(a), schemaComponent(b), ctx)) {
    return false;
  }
  const aDb = databaseComponent(a);
  const bDb = databaseComponent(b);
  if (aDb.folded && bDb.folded && !databaseComponentEq(aDb, bDb, ctx)) return false;
  return true;
}
